// Color utility functions

export const BRIGHTNESS_THRESHOLDS = {
  LIGHT: 200,
  SUBTLE: 150,
  MUTED: 100,
} as const;

type BrandFetchColor = { hex: string; type: string; brightness: number };

export function findNeutralColorsByBrightness(brandColors: BrandFetchColor[]): {
  light: string | undefined;
  subtle: string | undefined;
  muted: string | undefined;
  dark: string | undefined;
} {
  const { LIGHT, SUBTLE, MUTED } = BRIGHTNESS_THRESHOLDS;

  return {
    light: brandColors.find((c) => c.brightness > LIGHT)?.hex,
    subtle: brandColors.find(
      (c) => c.brightness > SUBTLE && c.brightness <= LIGHT,
    )?.hex,
    muted: brandColors.find(
      (c) => c.brightness > MUTED && c.brightness <= SUBTLE,
    )?.hex,
    dark: brandColors.find((c) => c.brightness <= MUTED)?.hex,
  };
}

/**
 * NOTE (Kevin, 2025-12-19, REPL-21177): Convert RGB/RGBA to hex.
 * Supports both comma-separated (rgb(255, 0, 0)) and space-separated (rgb(255 0 0)) syntax.
 */
export function rgbToHex(rgb: string): string | null {
  // NOTE (Kevin, 2025-12-19, REPL-21177): Try comma-separated first (legacy), then space-separated (modern CSS)
  const commaMatch = rgb.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  const spaceMatch = rgb.match(/rgba?\(\s*(\d+)\s+(\d+)\s+(\d+)/);
  const match = commaMatch ?? spaceMatch;

  if (!match) {
    return null;
  }

  const [, r, g, b] = match;
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(Number(r ?? 0))}${toHex(Number(g ?? 0))}${toHex(Number(b ?? 0))}`;
}

/**
 * Convert hex color to HSV color space
 * Needed to position color picker indicators (hue slider, saturation/value area)
 */
export function hexToHsv(hex: string) {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  const s = max === 0 ? 0 : diff / max;
  const v = max;

  if (diff !== 0) {
    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / diff + 2) / 6;
        break;
      case b:
        h = ((r - g) / diff + 4) / 6;
        break;
      default:
        h = 0;
        break;
    }
  }

  return { h: h * 360, s: s * 100, v: v * 100 };
}

/**
 * Convert HSV color space to hex color
 * Needed when user interacts with color picker (dragging hue/saturation/value)
 */
export function hsvToHex(h: number, s: number, v: number) {
  h = h / 360;
  s = s / 100;
  v = v / 100;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  let r, g, b;
  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
    default:
      r = g = b = 0;
  }

  const toHex = (n: number) => {
    return Math.round(n * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function srgbToHex(srgbColor: string): string | null {
  // Handle color(srgb r g b / alpha) format
  const srgbMatch = srgbColor.match(
    /color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+)?\)/,
  );
  if (!srgbMatch) {
    return null;
  }

  // Convert from 0-1 range to 0-255 range
  const r = Math.round(Number.parseFloat(srgbMatch[1] ?? "0") * 255);
  const g = Math.round(Number.parseFloat(srgbMatch[2] ?? "0") * 255);
  const b = Math.round(Number.parseFloat(srgbMatch[3] ?? "0") * 255);
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * NOTE (Kevin, 2025-12-18, REPL-21177): Converts 0-1 float RGB to hex. Used by OKLCH/OKLab
 * which use float values, unlike rgbToHex which parses "rgb(0-255, ...)" strings.
 */
function floatRgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => {
    return Math.round(Math.max(0, Math.min(1, n)) * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * NOTE (Kevin, 2025-12-19, REPL-21177): Convert OKLab to sRGB.
 * Internal helper used by oklchToRgb - OKLCH is the polar form of OKLab.
 */
function oklabToRgb(l: number, a: number, b: number): [number, number, number] {
  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.291485548 * b;

  const l3 = l_ * l_ * l_;
  const m3 = m_ * m_ * m_;
  const s3 = s_ * s_ * s_;

  const r = 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
  const g = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
  const bOut = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.707614701 * s3;

  const gamma = (x: number) => {
    return x <= 0.0031308 ? 12.92 * x : 1.055 * Math.pow(x, 1 / 2.4) - 0.055;
  };

  return [gamma(r), gamma(g), gamma(bOut)];
}

/**
 * NOTE (Kevin, 2025-12-18, REPL-21177): Convert OKLCH to sRGB.
 * OKLCH uses polar coordinates (chroma, hue) which we convert to OKLab's cartesian (a, b).
 */
function oklchToRgb(l: number, c: number, h: number): [number, number, number] {
  // NOTE (Kevin, 2025-12-18, REPL-21177): Convert hue to radians and OKLCH to OKLab
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);

  return oklabToRgb(l, a, b);
}

/**
 * Convert hex color to rgba string with specified alpha
 */
export function hexToRgba(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * NOTE (Kevin, 2025-12-19, REPL-21177): Normalize any CSS color format to hex.
 * Supports hex, rgb/rgba, srgb, and oklch. Returns original if conversion fails.
 */
export function normalizeColorToHex(
  color: string | null | undefined,
): string | null {
  if (!color) {
    return null;
  }

  if (color.startsWith("#")) {
    return color;
  }

  if (color.startsWith("rgb")) {
    return rgbToHex(color) ?? color;
  }

  if (color.startsWith("color(srgb")) {
    return srgbToHex(color) ?? color;
  }

  if (color.startsWith("oklch")) {
    const match = color.match(
      /oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*[\d.]+)?\s*\)/,
    );
    if (match) {
      const l = Number(match[1] ?? 0);
      const c = Number(match[2] ?? 0);
      const h = Number(match[3] ?? 0);
      const [r, g, b] = oklchToRgb(l, c, h);
      return floatRgbToHex(r, g, b);
    }
  }

  return color;
}
